/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { Form, Button, Tabs, Tab, Spinner, Badge } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import toast from "react-hot-toast";
import { ArrowLeft, UploadCloud, CheckCircle2, FileText, Image as ImageIcon, Gauge, Globe } from "lucide-react";
import "./stock.css";

const AddCar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const editId = queryParams.get("edit");

  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingObj, setUploadingObj] = useState({});

  const [formData, setFormData] = useState({
    make: "", model: "", year: "", chassis: "", color: "", mileage: "", mileageUnit: "km",
    auctionName: "", lotNumber: "", buyingPriceYen: "", cifPrice: "", status: "Available",
    buyerLeadId: "", soldBy: "",
    images: [], auctionSheet: "", eaaUrl: "", exportCertificateUrl: "",
    isPublicWebsite: true, transmission: "AUTOMATIC", fuelType: "PETROL", drive: "FWD"
  });

  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    const fetchSelectData = async () => {
      try {
        const [empRes, leadRes] = await Promise.all([
          api.get("/employees"),
          api.get("/leads?limit=5000")
        ]);
        setAgents(empRes.data.filter(e => e.role === "Agent" || e.role === "Manager"));
        setCustomers(leadRes.data.leads || []);
      } catch (err) {
        console.error("Failed to load select data", err);
      }
    };
    fetchSelectData();

    if (editId) {
      setLoading(true);
      api.get("/cars").then((res) => {
        const car = res.data.find(c => c.id === Number(editId));
        if (car) {
          setFormData({
            make: car.make || "", model: car.model || "", year: car.year || "", 
            chassis: car.chassis || "", color: car.color || "", 
            mileage: car.mileage != null ? car.mileage : "",
            mileageUnit: car.mileageUnit || "km",
            auctionName: car.auctionName || "", lotNumber: car.lotNumber || "",
            buyingPriceYen: car.buyingPriceYen || "", 
            cifPrice: car.cifPrice || "", status: car.status || "Available",
            buyerLeadId: car.buyerLeadId || "", soldBy: car.soldBy || "",
            images: car.images || [], auctionSheet: car.auctionSheet || "", 
            eaaUrl: car.eaaUrl || "", exportCertificateUrl: car.exportCertificateUrl || "",
            isPublicWebsite: car.isPublicWebsite !== undefined ? Boolean(car.isPublicWebsite) : true,
            transmission: car.transmission || "AUTOMATIC",
            fuelType: car.fuelType || "PETROL",
            drive: car.drive || "FWD"
          });
        }
        setLoading(false);
      });
    }
  }, [editId]);

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  // Generic Cloudinary Upload Handler
  const handleUpload = async (e, fieldName, isMultiple = false) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingObj(p => ({ ...p, [fieldName]: true }));
    try {
      const urls = [];
      for (let i = 0; i < files.length; i++) {
        const fileData = new FormData();
        fileData.append("file", files[i]);
        const res = await api.post("/upload", fileData, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(res.data.url);
      }

      if (isMultiple) {
        setFormData(p => ({ ...p, [fieldName]: [...p[fieldName], ...urls] }));
      } else {
        setFormData(p => ({ ...p, [fieldName]: urls[0] }));
      }
      toast.success(`${fieldName} uploaded!`);
    } catch (err) {
      toast.error(`Failed to upload ${fieldName}`);
    } finally {
      setUploadingObj(p => ({ ...p, [fieldName]: false }));
    }
  };

  const handleSave = async () => {
    if (!formData.make || !formData.model || !formData.year || !formData.chassis) {
      return toast.error("Make, Model, Year, and Chassis are required.");
    }

    setSaving(true);
    try {
      const payload = { 
        ...formData,
        year: formData.year ? Number(formData.year) : null,
        mileage: formData.mileage !== "" && formData.mileage !== null ? Number(formData.mileage) : null,
        mileageUnit: formData.mileageUnit || "km",
        lotNumber: formData.lotNumber ? String(formData.lotNumber).trim() : null,
        buyingPriceYen: formData.buyingPriceYen !== "" && formData.buyingPriceYen !== null ? Number(formData.buyingPriceYen) : null,
        cifPrice: formData.cifPrice !== "" && formData.cifPrice !== null ? Number(formData.cifPrice) : null,
        boughtPrice: formData.boughtPrice !== "" && formData.boughtPrice !== null ? Number(formData.boughtPrice) : null,
        soldPrice: formData.soldPrice !== "" && formData.soldPrice !== null ? Number(formData.soldPrice) : null,
        soldBy: formData.soldBy ? Number(formData.soldBy) : null,
        buyerLeadId: formData.buyerLeadId ? Number(formData.buyerLeadId) : null,
        isSold: formData.status === "Sold" ? 1 : 0
      };

      if (editId) {
        await api.put(`/cars/${editId}`, payload);
        toast.success("Car updated successfully!");
      } else {
        await api.post("/cars", payload);
        toast.success("Car added successfully!");
      }
      navigate("/dashboard/stock-list");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save car");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center p-5"><Spinner animation="border" /></div>;

  return (
    <div className="stock-page">
      <div className="stock-header mb-2">
        <Button variant="link" className="text-decoration-none p-0 text-muted d-flex align-items-center gap-1" onClick={() => navigate("/dashboard/stock-list")}>
          <ArrowLeft size={16} /> Back to Stock
        </Button>
      </div>

      <div className="card shadow-sm border-0" style={{ maxWidth: 900, margin: '0 auto', width: '100%' }}>
        <div className="card-header bg-transparent border-bottom pt-4 pb-3 px-4">
          <h2 className="stock-h1">{editId ? "Edit Vehicle Details" : "Add New Vehicle"}</h2>
          <div className="stock-sub">Complete the tabs below to build the vehicle profile.</div>
        </div>
        
        <div className="card-body p-0">
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="px-4 pt-3 border-bottom-0">
            <Tab eventKey="basic" title="1. Basic Details">
              <div className="p-4 bg-surface">
                <div className="row g-3">
                  <div className="col-md-6">
                    <Form.Label>Make *</Form.Label>
                    <Form.Control type="text" name="make" value={formData.make} onChange={handleChange} placeholder="e.g. Toyota" />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Model *</Form.Label>
                    <Form.Control type="text" name="model" value={formData.model} onChange={handleChange} placeholder="e.g. Prius" />
                  </div>
                  <div className="col-md-3">
                    <Form.Label>Year *</Form.Label>
                    <Form.Control type="number" name="year" value={formData.year} onChange={handleChange} placeholder="e.g. 2021" />
                  </div>
                  <div className="col-md-3">
                    <Form.Label>Mileage</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-surface-2 border-end-0 text-muted" style={{ fontSize: '13px' }}>
                        <Gauge size={14} />
                      </span>
                      <Form.Control 
                        type="number" 
                        name="mileage" 
                        value={formData.mileage} 
                        onChange={handleChange} 
                        placeholder="e.g. 45000" 
                        className="border-start-0 border-end-0" 
                      />
                      <Form.Select
                        name="mileageUnit"
                        value={formData.mileageUnit || "km"}
                        onChange={handleChange}
                        style={{ maxWidth: '82px', fontSize: '12px', fontWeight: 600, background: 'var(--surface-2)', color: 'var(--text)', borderLeft: '1px solid var(--border)' }}
                      >
                        <option value="km">KM</option>
                        <option value="miles">Miles</option>
                      </Form.Select>
                    </div>
                    {formData.mileage !== "" && formData.mileage !== null && (
                      <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                        {formData.mileageUnit === 'miles'
                          ? `≈ ${Math.round(Number(formData.mileage) * 1.60934).toLocaleString()} km`
                          : `≈ ${Math.round(Number(formData.mileage) * 0.621371).toLocaleString()} miles`
                        }
                      </div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <Form.Label>Chassis *</Form.Label>
                    <Form.Control type="text" name="chassis" value={formData.chassis} onChange={handleChange} placeholder="e.g. ZVW50-123456" />
                  </div>
                  <div className="col-md-3">
                    <Form.Label>Color</Form.Label>
                    <Form.Control type="text" name="color" value={formData.color} onChange={handleChange} placeholder="e.g. Pearl White" />
                  </div>

                  <div className="col-md-4">
                    <Form.Label>Transmission</Form.Label>
                    <Form.Select name="transmission" value={formData.transmission} onChange={handleChange}>
                      <option value="AUTOMATIC">Automatic</option>
                      <option value="MANUAL">Manual</option>
                      <option value="CVT">CVT</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-4">
                    <Form.Label>Fuel Type</Form.Label>
                    <Form.Select name="fuelType" value={formData.fuelType} onChange={handleChange}>
                      <option value="PETROL">Petrol / Gasoline</option>
                      <option value="DIESEL">Diesel</option>
                      <option value="HYBRID">Hybrid</option>
                      <option value="ELECTRIC">Electric</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-4">
                    <Form.Label>Drivetrain</Form.Label>
                    <Form.Select name="drive" value={formData.drive} onChange={handleChange}>
                      <option value="FWD">Front-Wheel Drive (FWD)</option>
                      <option value="RWD">Rear-Wheel Drive (RWD)</option>
                      <option value="AWD">All-Wheel Drive (AWD)</option>
                      <option value="FOUR_WD">4WD</option>
                    </Form.Select>
                  </div>

                  {/* PAJ Public Website Publishing Switch */}
                  <div className="col-12 mt-3">
                    <div className="d-flex align-items-center justify-content-between p-3 rounded-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                      <div>
                        <div className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: '13.5px', color: 'var(--text)' }}>
                          <Globe size={16} className="text-primary" /> Publish to PAJ Public Website
                        </div>
                        <div className="small text-muted" style={{ fontSize: '12px' }}>
                          When enabled, this vehicle is automatically published to the public inventory catalog on the PAJ website.
                        </div>
                      </div>
                      <Form.Check 
                        type="switch"
                        id="publish-website-switch"
                        checked={formData.isPublicWebsite}
                        onChange={(e) => setFormData(p => ({ ...p, isPublicWebsite: e.target.checked }))}
                        style={{ transform: 'scale(1.25)', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-end">
                  <Button onClick={() => setActiveTab("pricing")}>Next: Pricing</Button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="pricing" title="2. Pricing & Auction">
              <div className="p-4 bg-surface">
                <div className="row g-3">
                  <div className="col-md-6">
                    <Form.Label>Auction Name / Source</Form.Label>
                    <Form.Control type="text" name="auctionName" value={formData.auctionName} onChange={handleChange} placeholder="e.g. USS Tokyo / Honda Nagoya" />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Auction Lot Number</Form.Label>
                    <Form.Control type="text" name="lotNumber" value={formData.lotNumber} onChange={handleChange} placeholder="e.g. 10245" />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Buying Price (JPY)</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-surface-2 border-end-0">¥</span>
                      <Form.Control type="number" name="buyingPriceYen" value={formData.buyingPriceYen} onChange={handleChange} placeholder="0" className="border-start-0" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Base CIF Price (USD)</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-surface-2 border-end-0">$</span>
                      <Form.Control type="number" name="cifPrice" value={formData.cifPrice} onChange={handleChange} placeholder="0" className="border-start-0" />
                    </div>
                    <Form.Text className="text-muted">The minimum price before agent profit.</Form.Text>
                  </div>
                </div>
                <div className="mt-4 d-flex justify-content-between">
                  <Button variant="outline-secondary" onClick={() => setActiveTab("basic")}>Back</Button>
                  <Button onClick={() => setActiveTab("status")}>Next: Status</Button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="status" title="3. Status & Sale">
              <div className="p-4 bg-surface">
                <div className="row g-3">
                  <div className="col-md-12">
                    <Form.Label>Vehicle Status</Form.Label>
                    <Form.Select name="status" value={formData.status} onChange={handleChange}>
                      <option value="Available">Available (In Stock)</option>
                      <option value="Reserved">Reserved</option>
                      <option value="Sold">Sold</option>
                    </Form.Select>
                  </div>
                  
                  {formData.status === 'Sold' && (
                    <>
                      <div className="col-md-6 mt-4">
                        <Form.Label>Sold To Customer</Form.Label>
                        <Form.Select name="buyerLeadId" value={formData.buyerLeadId} onChange={handleChange}>
                          <option value="">-- Select Customer --</option>
                          {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>)}
                        </Form.Select>
                      </div>
                      <div className="col-md-6 mt-4">
                        <Form.Label>Sold By Agent</Form.Label>
                        <Form.Select name="soldBy" value={formData.soldBy} onChange={handleChange}>
                          <option value="">-- Select Agent --</option>
                          {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </Form.Select>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-4 d-flex justify-content-between">
                  <Button variant="outline-secondary" onClick={() => setActiveTab("pricing")}>Back</Button>
                  <Button onClick={() => setActiveTab("media")}>Next: Media</Button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="media" title="4. Media & Docs">
              <div className="p-4 bg-surface">
                <div className="stock-modal-docs-grid">
                  
                  {/* Car Images */}
                  <div className="stock-modal-upload-box" style={{ gridColumn: '1 / -1' }}>
                    <label className="d-block w-100" style={{ cursor: 'pointer' }}>
                      <ImageIcon size={28} className="text-primary mb-2" />
                      <div className="fw-bold">Upload Car Photos</div>
                      <div className="small text-muted mb-3">Select multiple images to create gallery</div>
                      <input type="file" multiple accept="image/*" onChange={(e) => handleUpload(e, 'images', true)} disabled={uploadingObj.images} />
                      {uploadingObj.images ? <Spinner size="sm" /> : <Button variant="outline-primary" size="sm" as="span">Browse Files</Button>}
                    </label>
                    {formData.images.length > 0 && (
                      <div className="d-flex gap-2 mt-3 overflow-auto">
                        {formData.images.map((img, i) => (
                          <img key={i} src={img} alt={`car-${i}`} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Auction Sheet */}
                  <div className="stock-modal-upload-box">
                    <label className="d-block w-100" style={{ cursor: 'pointer' }}>
                      <FileText size={24} className="text-warning mb-2" />
                      <div className="fw-bold">Auction Sheet</div>
                      <input type="file" accept=".pdf,image/*" onChange={(e) => handleUpload(e, 'auctionSheet')} disabled={uploadingObj.auctionSheet} />
                      {uploadingObj.auctionSheet ? <Spinner size="sm" /> : formData.auctionSheet ? <Badge bg="success"><CheckCircle2 size={12}/> Uploaded</Badge> : <span className="small text-muted text-decoration-underline">Click to upload</span>}
                    </label>
                  </div>

                  {/* Export Certificate */}
                  <div className="stock-modal-upload-box">
                    <label className="d-block w-100" style={{ cursor: 'pointer' }}>
                      <FileText size={24} className="text-info mb-2" />
                      <div className="fw-bold">Export Certificate (EC)</div>
                      <input type="file" accept=".pdf,image/*" onChange={(e) => handleUpload(e, 'exportCertificateUrl')} disabled={uploadingObj.exportCertificateUrl} />
                      {uploadingObj.exportCertificateUrl ? <Spinner size="sm" /> : formData.exportCertificateUrl ? <Badge bg="success"><CheckCircle2 size={12}/> Uploaded</Badge> : <span className="small text-muted text-decoration-underline">Click to upload</span>}
                    </label>
                  </div>

                  {/* EAA */}
                  <div className="stock-modal-upload-box">
                    <label className="d-block w-100" style={{ cursor: 'pointer' }}>
                      <FileText size={24} className="text-danger mb-2" />
                      <div className="fw-bold">EAA Document</div>
                      <input type="file" accept=".pdf,image/*" onChange={(e) => handleUpload(e, 'eaaUrl')} disabled={uploadingObj.eaaUrl} />
                      {uploadingObj.eaaUrl ? <Spinner size="sm" /> : formData.eaaUrl ? <Badge bg="success"><CheckCircle2 size={12}/> Uploaded</Badge> : <span className="small text-muted text-decoration-underline">Click to upload</span>}
                    </label>
                  </div>

                </div>
                
                <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                  <Button variant="outline-secondary" onClick={() => setActiveTab("status")}>Back</Button>
                  <Button variant="success" size="lg" className="px-5 fw-bold" onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner size="sm" /> : editId ? "Save Changes" : "Publish to Stock"}
                  </Button>
                </div>
              </div>
            </Tab>

          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AddCar;
